/* eslint-disable camelcase */
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { getAxiosErrorMessage } from "../../../../../api/axiosError";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useState } from "react";
import { MembershipAddFormData } from "../../../../hooks/useCsvUserAddValidation";
import { OrganizationMembershipUserData } from "../../../../../models/organizationMembershipData";
import {
  createUserMembership,
  createUserBulkMembership,
  fetchMembershipsByOrganizationId,
} from "../../../../../redux/organizationMembership/action";
import Loader from "../../../../theming/loader/Loader";
import { Stateful } from "./CsvBulkUpload";

/**
 * @interface CsvBulkUploadStateProps
 */
interface CsvBulkUploadStateProps {
  bulkAddFinished: Stateful<boolean>;
  bulkAddedCount: Stateful<number>;
  bulkBookingLoading: Stateful<boolean>;
}

/**
 * @interface CsvBulkUploadProps
 */
interface CsvBulkUploadProps extends CsvBulkUploadStateProps {
  membersToAdd: MembershipAddFormData[];
  // eslint-disable-next-line no-unused-vars
  setValidMembersToAdd: (membersToAdd: MembershipAddFormData[]) => void;
  setShowUploadSection: React.Dispatch<React.SetStateAction<boolean>>;
}

type BulkMembersAddedResult = true | { success: false; error: unknown };

type MemberAddStatus =
  | "added"
  | "already added"
  | "username not exists"
  | "username already taken"
  | "skipped"
  | "invalid data"
  | "failed";
/**
 * Component for displaying a table of bulk membership additions.
 * @param {CsvBulkUploadProps} props - The component props.
 * @returns {JSX.Element} - The rendered component.
 */
const BulkMembershipOverviewList: React.FC<CsvBulkUploadProps> = ({
  membersToAdd,
  setValidMembersToAdd,
  setShowUploadSection,
  bulkAddFinished,
  bulkAddedCount,
  bulkBookingLoading,
}: CsvBulkUploadProps): JSX.Element => {
  const { t } = useTranslation(["common", "organization", "snackbars"]);

  const dispatch = useDispatch();
  const [singleAddLoadingIndex, setSingleAddLoadingIndex] = useState<number | null>(
    null
  );
  /**
   * Handles the deletion of a membership to be added.
   * @param {number} index - The index of the user to delete.
   * @returns {void}
   */
  const handleDeleteAddMember = (index: number) => {
    const updatedMembersToBeAdded = [...membersToAdd];
    updatedMembersToBeAdded.splice(index, 1);
    setValidMembersToAdd(updatedMembersToBeAdded);
  };

  /**
   * Extracts the HTTP status code from an error message string,
   * such as: "Request failed with status code 409"
   *
   * @param message - The error message string to extract from
   * @returns The status code as a number (e.g. 409), or null if not found
   */
  const extractStatusCodeFromMessage = (message: unknown): number | null => {
    if (typeof message !== "string") return null;

    const match = message.match(/status code (\d{3})/);
    return match ? parseInt(match[1], 10) : null;
  };

  /**
   * Maps an HTTP status code to a corresponding member add status.
   *
   * @param statusCode - The HTTP response status code from the backend.
   * @param message - The HTTP response message from the backend.
   * @returns A status string representing the result of the add attempt.
   *
   * - 422 → "invalid data"
   * - Any other code → "failed"
   */
  const getMemberStatusFromStatusCode = (
    statusCode: number | null,
    message: string
  ): MemberAddStatus => {
    if (statusCode === 409) {
      if (message.includes("backendResponses.error.usernameAlreadyExists")) {
        return "username already taken";
      }

      return "already added";
    }

    if (statusCode === 422) return "invalid data";

    if (
      statusCode === 400 &&
      message.includes("backendResponses.error.usernameNotExists")
    ) {
      return "username not exists";
    }

    return "failed";
  };

  /**
   * Handles adding a user to membership.
   * @param {MembershipAddFormData} values - The form data containing user information.
   * @param {number} index - The index of the member to be added.
   * @returns {void}
   */
  const handleAddMember = async (values: MembershipAddFormData, index: number) => {
    setSingleAddLoadingIndex(index);
    try {
      const data: OrganizationMembershipUserData = {
        first_name: values?.first_name,
        last_name: values?.last_name,
        email: values?.email,
        username: "",
        birthday: values?.birthday,
        street: values?.street,
        street_number: values?.street_number,
        zip_code: values?.zip_code,
        city: values?.city,
        country: values?.country,
        organization_id: values?.organization_id,
      };
      const actionResult = await dispatch(createUserMembership(data));
      const raw = (actionResult as any).error;
      const detail = getAxiosErrorMessage(raw, raw?.message || "");

      const updatedMembersToBeAdded = [...membersToAdd];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (actionResult === true) {
        updatedMembersToBeAdded[index].status = "added";
        setValidMembersToAdd(updatedMembersToBeAdded);
      } else if (
        typeof detail === "string" &&
        detail.includes("backendResponses.error.noSlotsAvailable")
      ) {
        updatedMembersToBeAdded.splice(index, 1);
      } else {
        const statusCode = extractStatusCodeFromMessage(raw.message);
        updatedMembersToBeAdded[index].status = getMemberStatusFromStatusCode(
          statusCode,
          detail
        );
        setValidMembersToAdd(updatedMembersToBeAdded);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setSingleAddLoadingIndex(null);
    }
  };

  /**
   * Handles bulk adding user memberships.
   * @param {OrganizationMembershipUserData[]} membershipToBeAdded- The list of members to be added to process in bulk.
   * @returns {void}
   */
  const handleBulkAddMembers = async (
    membershipToBeAdded: OrganizationMembershipUserData[]
  ) => {
    try {
      bulkBookingLoading.setValue(true);
      const updatedMembersToBeAdded = [...membershipToBeAdded];
      let addedCount = 0;
      for (let index = membershipToBeAdded.length - 1; index >= 0; index--) {
        const memberToBeAdded = membershipToBeAdded[index];

        const actionResult = await dispatch(createUserBulkMembership(memberToBeAdded));
        const result = actionResult as unknown as BulkMembersAddedResult;
        // Get current members + members to be added from Redux after each insert
        await dispatch(
          fetchMembershipsByOrganizationId(memberToBeAdded.organization_id)
        );

        if (result === true) {
          addedCount++;
          updatedMembersToBeAdded[index].status = "added";
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const raw = (result as any).error;
          const detail = getAxiosErrorMessage(raw, raw?.message || "");

          if (
            typeof detail === "string" &&
            detail.includes("backendResponses.error.noSlotsAvailable")
          ) {
            updatedMembersToBeAdded[index].status = "skipped";
          } else {
            const statusCode = extractStatusCodeFromMessage(raw.message);
            updatedMembersToBeAdded[index].status = getMemberStatusFromStatusCode(
              statusCode,
              detail
            );
          }
        }
      }
      setValidMembersToAdd([...updatedMembersToBeAdded]);
      bulkAddedCount.setValue(addedCount);
    } catch (error) {
      enqueueSnackbar((error as Error)?.message || "Bulk adding members failed", {
        variant: "error",
      });
    } finally {
      bulkBookingLoading.setValue(false);
      bulkAddFinished.setValue(true);
    }
  };

  const allMembersProcessed = membersToAdd.every((m) => !!m.status);
  const hasPendingMembers = membersToAdd.some((member) => member.status !== "added");

  return (
    <>
      <Box style={{ display: "flex", justifyContent: "flex-end" }}>
        {/* Show message if bulk add finished */}
        {!allMembersProcessed ? (
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={() => {
              setValidMembersToAdd([]);
              setShowUploadSection(true);
            }}
            style={{ marginRight: "1rem" }}
          >
            {t("membership.clearList", { ns: "organization" })}
          </Button>
        ) : null}

        {!allMembersProcessed ? (
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={() => handleBulkAddMembers(membersToAdd)}
          >
            {t("membership.addAll", { ns: "organization" })}
          </Button>
        ) : null}
      </Box>

      {bulkAddFinished.value && bulkAddedCount.value > 0 ? (
        <Box style={{ marginBottom: "1rem", paddingTop: "1rem", paddingBottom: "1rem" }}>
          <Typography style={{ fontWeight: "bold" }}>
            {t("membership.bulkAddSuccess", {
              count: bulkAddedCount.value,
              ns: "organization",
            })}
          </Typography>
        </Box>
      ) : null}

      {bulkBookingLoading.value ? (
        <Loader />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("profile.firstName", { ns: "common" })}</TableCell>
              <TableCell>{t("profile.lastName", { ns: "common" })}</TableCell>
              <TableCell>{t("profile.userAccountName", { ns: "common" })}</TableCell>
              <TableCell>{t("membership.status", { ns: "organization" })}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasPendingMembers ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography>
                    {t("membership.allMembersProcessed", { ns: "organization" })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              membersToAdd
                .filter((member) => member.status !== "added")
                .map((memberToAdd, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell>{memberToAdd?.first_name}</TableCell>
                    <TableCell>{memberToAdd?.last_name}</TableCell>
                    <TableCell>{memberToAdd?.email || memberToAdd?.username}</TableCell>
                    <TableCell style={{ maxWidth: "8rem" }}>
                      {memberToAdd?.status === "skipped" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.tooManyMembersSkipped", {
                            ns: "organization",
                          })}
                        </Typography>
                      )}
                      {memberToAdd?.status === "already added" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.alreadyMember", { ns: "organization" })}
                        </Typography>
                      )}
                      {memberToAdd?.status === "username already taken" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.usernameAlreadyTaken", { ns: "organization" })}
                        </Typography>
                      )}
                      {memberToAdd?.status === "username not exists" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.usernameNotExists", { ns: "organization" })}
                        </Typography>
                      )}
                      {memberToAdd?.status === "invalid data" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.invalidData", { ns: "organization" })}
                        </Typography>
                      )}
                      {memberToAdd?.status === "failed" && (
                        <Typography color="error" style={{ fontWeight: 600 }}>
                          {t("membership.failedToAdd", { ns: "organization" })}
                        </Typography>
                      )}
                      {!memberToAdd?.status && (
                        <Button
                          size="large"
                          variant="contained"
                          color="primary"
                          onClick={() => handleAddMember(memberToAdd, index)}
                          disabled={singleAddLoadingIndex === index}
                          style={{ width: "100%" }}
                        >
                          {singleAddLoadingIndex === index ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            t("membership.add", { ns: "organization" })
                          )}

                        </Button>
                      )}
                    </TableCell>

                    {!memberToAdd?.status && (
                      <TableCell>
                        <Button
                          size="large"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleDeleteAddMember(index)}
                          disabled={singleAddLoadingIndex === index}
                        >
                          {t("delete", { ns: "common" })}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default BulkMembershipOverviewList;
